import { lazy } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'

import { AppLayout } from '../layouts/AppLayout'
import { AdminLayout } from '../layouts/AdminLayout'
import { ProtectedRoute } from '../components/ProtectedRoute'
import { AdminRoute } from '../components/AdminRoute'
import { RouteErrorPage } from '../components/RouteErrorPage'

import {
  LazyWrapper,
  RootLayout,
  AdminDashboardWrapper,
  AlgoliaManagementWrapper,
  RecommendationLogsWrapper,
  AnalyticsViewerWrapper,
  RecommendationTestingWrapper,
  UseCaseAnalyticsWrapper,
  MediaManagementWrapper,
  UserManagementWrapper,
  EvalDashboardWrapper,
  EvalFixtureBuilderWrapper,
  RetrievalReviewWrapper,
  QuickAddPopupWrapper,
} from './components'

// Import Firebase initialization
import '../firebase'

// Lazy load components
const MainDashboard = lazy(() => import('../pages/MainDashboard'))
const SearchPage = lazy(() => import('../pages/SearchPage'))
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'))
const TermsOfUse = lazy(() => import('../pages/TermsOfUse'))

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <RootLayout>
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      </RootLayout>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <MainDashboard />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage />,
      },
      {
        path: 'search',
        element: (
          <LazyWrapper>
            <SearchPage />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage />,
      },
    ],
  },
  {
    path: '/privacy',
    element: (
      <RootLayout>
        <LazyWrapper>
          <PrivacyPolicy />
        </LazyWrapper>
      </RootLayout>
    ),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/terms',
    element: (
      <RootLayout>
        <LazyWrapper>
          <TermsOfUse />
        </LazyWrapper>
      </RootLayout>
    ),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/admin',
    element: (
      <RootLayout>
        <AdminRoute>
          <AdminLayout />
        </AdminRoute>
      </RootLayout>
    ),
    errorElement: <RouteErrorPage showTechnicalDetails={true} />,
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <AdminDashboardWrapper />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage showTechnicalDetails={true} />,
      },
      {
        path: 'algolia',
        element: (
          <LazyWrapper>
            <AlgoliaManagementWrapper />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage showTechnicalDetails={true} />,
      },
      {
        path: 'logs',
        element: (
          <LazyWrapper>
            <RecommendationLogsWrapper />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage showTechnicalDetails={true} />,
      },
      {
        path: 'analytics',
        element: (
          <LazyWrapper>
            <AnalyticsViewerWrapper />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage showTechnicalDetails={true} />,
      },
      {
        path: 'testing',
        element: (
          <LazyWrapper>
            <RecommendationTestingWrapper />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage showTechnicalDetails={true} />,
      },
      {
        path: 'use-cases',
        element: (
          <LazyWrapper>
            <UseCaseAnalyticsWrapper />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage showTechnicalDetails={true} />,
      },
      {
        path: 'media-management',
        element: (
          <LazyWrapper>
            <MediaManagementWrapper />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage showTechnicalDetails={true} />,
      },
      {
        path: 'user-management',
        element: (
          <LazyWrapper>
            <UserManagementWrapper />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage showTechnicalDetails={true} />,
      },
      {
        path: 'eval',
        element: (
          <LazyWrapper>
            <EvalDashboardWrapper />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage showTechnicalDetails={true} />,
      },
      {
        path: 'eval-fixtures',
        element: (
          <LazyWrapper>
            <EvalFixtureBuilderWrapper />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage showTechnicalDetails={true} />,
      },
      {
        path: 'curation',
        element: (
          <LazyWrapper>
            <RetrievalReviewWrapper />
          </LazyWrapper>
        ),
        errorElement: <RouteErrorPage showTechnicalDetails={true} />,
      },
    ],
  },
  {
    // Compact, chrome-less popup opened by the "Add to Cyrano" bookmarklet.
    // Admin-protected like the rest of /admin, but rendered without AdminLayout
    // so the small window shows only the upload status card.
    path: '/admin/quick-add',
    element: (
      <RootLayout>
        <AdminRoute>
          <LazyWrapper>
            <QuickAddPopupWrapper />
          </LazyWrapper>
        </AdminRoute>
      </RootLayout>
    ),
    errorElement: <RouteErrorPage showTechnicalDetails={true} />,
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
    errorElement: <RouteErrorPage />,
  },
])
